import { TOrganizationTypes } from '@sravni/ugc/lib/organizations/types';

import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import { OLD_ORG_TYPE_MAP } from '../../components/CreateReview/helpers/oldOrgTypeMap';
import { CUSTOMER_TYPES } from '../../constants/reviews';
import { IItem } from '../../interfaces/organizations';
import { TProduct } from '../../server/controllers/products';
import { loadProductsByTag } from '../../services/products';
import { loadServices } from '../../services/services';
import { TService, TServicesByCustomerType } from '../../types/services';
import { GlobalState } from '../index';

export interface IState {
  serviceList: TService[];
  productList: TProduct[];

  organization?: IItem;
  service?: TService;
  product?: TProduct;

  reviewId?: number;
}

const initialState: IState = {
  serviceList: null,
  productList: null,
};

const name = 'create-review-data';

const slice = createSlice({
  name,
  initialState,
  reducers: {
    setServiceList(state, { payload }: { payload: TService[] }) {
      state.serviceList = payload;
    },
    setProductList(state, { payload }: { payload: TProduct[] }) {
      state.productList = payload;
    },
    setReviewId(state, { payload }: { payload: number }) {
      state.reviewId = payload;
    },
    setOrganization(state, { payload }: { payload: IItem }) {
      state.organization = payload;
    },
    setService(state, { payload }: { payload: TService }) {
      state.service = payload;
    },
    setProduct(state, { payload }: { payload: TProduct }) {
      state.product = payload;
    },
    findAndSetService(state, { payload }: { payload: string }) {
      const newService = state.serviceList?.find((service) => service.key === payload);
      state.service = newService;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchServiceList.fulfilled, (state, { payload }) => {
      state.serviceList = payload[CUSTOMER_TYPES.All];
    });
    builder.addCase(fetchServiceList.rejected, (state) => {
      state.serviceList = null;
    });
    builder.addCase(fetchServiceList.pending, (state) => {
      state.serviceList = null;
    });
    builder.addCase(fetchProductList.fulfilled, (state, { payload }) => {
      state.productList = payload;
    });
    builder.addCase(fetchProductList.pending, (state) => {
      state.productList = null;
    });
    builder.addCase(fetchProductList.rejected, (state) => {
      state.productList = null;
    });
    builder.addCase(findAndSetOrganization.fulfilled, (state, { payload }) => {
      state.organization = payload;
    });
  },
});

const { reducer, actions } = slice;

export const { setServiceList, setProductList, setReviewId, setOrganization, findAndSetService, setProduct } = actions;

export const fetchServiceList = createAsyncThunk<TServicesByCustomerType, TOrganizationTypes>(
  `${name}/fetchServiceList`,
  async (payload) => {
    return await loadServices(payload);
  },
);

export const findAndSetOrganization = createAsyncThunk<IItem, string, { state: GlobalState }>(
  `${name}/findAndSetOrganization`,
  (payload, { getState }) => {
    const organization = getState().organizations.organizationsList?.find((org) => org.id.toString() === payload);

    return organization;
  },
);

export const fetchProductList = createAsyncThunk<
  TProduct[],
  {
    service: string;
    orgId: string;
    orgType: TOrganizationTypes;
  }
>(`${name}/fetchProductList`, async ({ service, orgType, orgId }) => {
  return await loadProductsByTag({
    tag: service,
    id: orgId,
    type: OLD_ORG_TYPE_MAP[orgType],
  });
});

export default reducer;
